import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";

const ARTICLE_URL = "https://www.hey-lets-travel.com/community/eastenderz-hi-ibiza-9-june-2026";
const IMAGE_URL = "https://www.hey-lets-travel.com/assets/community/eastenderz-hi-ibiza-9-june-2026.png";
const TICKET_URL = "https://www.ibiza-spotlight.com/night/promoters/eastenderz-hi";
const CONCIERGE_URL = "/concierge";
const EVENTS_URL = "/events";

const THEATRE_LINEUP = ["East End Dubs", "Patrick Topping", "Wheats", "Stef Davidse"];
const CLUB_ROOM_LINEUP = ["Paco Osuna", "Sosa", "Gruuvelements"];
const ALL_ARTISTS = [...THEATRE_LINEUP, ...CLUB_ROOM_LINEUP];

const KEYWORDS = [
  "Eastenderz Hi Ibiza 2026",
  "Eastenderz Hï Ibiza tickets",
  "East End Dubs Ibiza 2026",
  "Eastenderz 9 June Ibiza",
  "Patrick Topping Hi Ibiza",
  "Wheats Ibiza 2026",
  "Stef Davidse Ibiza",
  "Paco Osuna NOW HERE Hi Ibiza",
  "Sosa Ibiza 2026",
  "Gruuvelements Hi Ibiza",
  "Hi Ibiza Tuesday 2026",
  "Ibiza house music events June 2026",
  "Ibiza deep tech events 2026",
  "minimal house Ibiza 2026",
  "Ibiza ticket and hotel package",
  "HLT Ibiza concierge",
  "Ibiza hotels near Hi Ibiza",
];

const FAQS = [
  {
    question: "When is East End Dubs presents Eastenderz at Hi Ibiza?",
    answer: "This Eastenderz date takes place at Hi Ibiza on Tuesday 9 June 2026, with the event listed from 23:30.",
  },
  {
    question: "Who is playing Eastenderz at Hi Ibiza on 9 June?",
    answer: "The Theatre lineup includes East End Dubs, Patrick Topping, Wheats and Stef Davidse. Club Room / NOW HERE lists Paco Osuna, Sosa and Gruuvelements.",
  },
  {
    question: "Where is Eastenderz taking place in Ibiza?",
    answer: "Eastenderz takes place at Hi Ibiza in Playa d'en Bossa, one of the island's main nightlife areas.",
  },
  {
    question: "What music should I expect at Eastenderz?",
    answer: "Expect deep tech, minimal house, rolling tech house, garage-influenced grooves and underground house music built for a serious Ibiza dancefloor.",
  },
  {
    question: "Can HLT help with hotels and concierge for Eastenderz?",
    answer: "Yes. HLT can help with ticket direction, Ibiza hotels, villas, transfers, VIP extras, group planning and full concierge packages around Eastenderz at Hi Ibiza.",
  },
];

const articleSchema = {
  "@context": "https://schema.org",
  "@type": "Article",
  headline: "East End Dubs Presents Eastenderz at Hi Ibiza: 9 June 2026 Guide",
  description:
    "East End Dubs presents Eastenderz at Hi Ibiza on Tuesday 9 June 2026 with East End Dubs, Patrick Topping, Wheats, Stef Davidse, Paco Osuna, Sosa and Gruuvelements.",
  image: [IMAGE_URL],
  mainEntityOfPage: ARTICLE_URL,
  datePublished: "2026-06-09",
  dateModified: "2026-06-09",
  author: { "@type": "Organization", name: "Hey Let's Travel" },
  publisher: { "@type": "Organization", name: "Hey Let's Travel" },
};

const eventSchema = {
  "@context": "https://schema.org",
  "@type": "MusicEvent",
  name: "East End Dubs presents Eastenderz at Hi Ibiza",
  startDate: "2026-06-09T23:30:00+02:00",
  eventAttendanceMode: "https://schema.org/OfflineEventAttendanceMode",
  eventStatus: "https://schema.org/EventScheduled",
  image: [IMAGE_URL],
  location: {
    "@type": "Place",
    name: "Hi Ibiza",
    address: "Playa d'en Bossa, Ibiza, Spain",
  },
  performer: ALL_ARTISTS.map((artist) => ({ "@type": "MusicGroup", name: artist })),
  offers: {
    "@type": "Offer",
    url: TICKET_URL,
    priceCurrency: "GBP",
    price: "25.99",
    availability: "https://schema.org/InStock",
  },
};

const faqSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: FAQS.map((item) => ({
    "@type": "Question",
    name: item.question,
    acceptedAnswer: { "@type": "Answer", text: item.answer },
  })),
};

function TicketCta({ children = "View Eastenderz Tickets" }) {
  return (
    <a href={TICKET_URL} target="_blank" rel="noopener noreferrer" className="btn-primary">
      {children} -&gt;
    </a>
  );
}

function ConciergeCta({ children = "Plan Eastenderz with HLT" }) {
  return (
    <Link to={CONCIERGE_URL} className="btn-secondary">
      {children} -&gt;
    </Link>
  );
}

function ArticleSection({ title, children }) {
  return (
    <section className="space-y-4 border-t border-white/10 pt-8">
      <h2 className="font-display text-4xl leading-none text-white md:text-5xl">{title}</h2>
      <div className="space-y-4 text-base leading-8 text-slate-300">{children}</div>
    </section>
  );
}

export default function EastenderzHiIbizaJuneArticle() {
  return (
    <>
      <Helmet>
        <title>Eastenderz Hi Ibiza 2026 Tickets | 9 June East End Dubs Guide</title>
        <meta
          name="description"
          content="East End Dubs presents Eastenderz at Hi Ibiza on Tuesday 9 June 2026. Lineup includes East End Dubs, Patrick Topping, Wheats, Stef Davidse, Paco Osuna, Sosa and Gruuvelements."
        />
        <meta name="keywords" content={KEYWORDS.join(", ")} />
        <link rel="canonical" href={ARTICLE_URL} />
        <meta property="og:type" content="article" />
        <meta property="og:title" content="Eastenderz Hi Ibiza 2026 Tickets | 9 June East End Dubs Guide" />
        <meta property="og:description" content="Plan Eastenderz at Hi Ibiza with HLT ticket direction, hotels, VIP support and concierge planning." />
        <meta property="og:url" content={ARTICLE_URL} />
        <meta property="og:image" content={IMAGE_URL} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Eastenderz Hi Ibiza 2026 Tickets" />
        <meta name="twitter:description" content="East End Dubs, Patrick Topping, Wheats and Stef Davidse take The Theatre at Hi Ibiza on Tuesday 9 June 2026." />
        <meta name="twitter:image" content={IMAGE_URL} />
        <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(eventSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      </Helmet>

      <article className="page space-y-10">
        <header className="grid gap-6 lg:grid-cols-[minmax(0,1fr),30rem] lg:items-end">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.3em] text-brand-500">Ibiza 2026 guide</p>
            <h1 className="mt-4 font-display text-5xl leading-none text-white md:text-7xl">
              Eastenderz at Hi Ibiza: 9 June 2026
            </h1>
            <p className="mt-5 max-w-3xl text-lg leading-8 text-slate-300">
              East End Dubs presents Eastenderz at Hi Ibiza on Tuesday 9 June 2026, bringing East End Dubs, Patrick Topping, Wheats and Stef Davidse to The Theatre, with Paco Osuna, Sosa and Gruuvelements in Club Room / NOW HERE.
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <TicketCta children="View Eastenderz ticket options" />
              <ConciergeCta children="Build the Ibiza trip with HLT" />
            </div>
          </div>
          <aside className="border border-brand-500/25 bg-[#111111] p-5">
            <p className="text-xs uppercase tracking-[0.22em] text-brand-500">Event details</p>
            <dl className="mt-4 grid gap-3 text-sm text-slate-300">
              <div><dt className="text-white">Event</dt><dd>East End Dubs presents Eastenderz</dd></div>
              <div><dt className="text-white">Venue</dt><dd>Hi Ibiza</dd></div>
              <div><dt className="text-white">Date</dt><dd>Tuesday 9 June 2026</dd></div>
              <div><dt className="text-white">Time</dt><dd>23:30</dd></div>
              <div><dt className="text-white">From</dt><dd>GBP 25.99</dd></div>
              <div><dt className="text-white">Sound</dt><dd>Deep tech, minimal house, tech house and rolling underground groove</dd></div>
            </dl>
          </aside>
        </header>

        <a href={TICKET_URL} target="_blank" rel="noopener noreferrer" className="group block overflow-hidden border border-brand-500/25 bg-black shadow-[0_24px_90px_rgba(0,0,0,0.55)] transition hover:border-brand-500/70">
          <img src="/assets/community/eastenderz-hi-ibiza-9-june-2026.png" alt="Eastenderz at Hi Ibiza 9 June event poster" className="block h-auto w-full transition duration-500 group-hover:scale-[1.01]" />
        </a>

        <div className="mx-auto max-w-4xl space-y-10">
          <section className="space-y-4 text-base leading-8 text-slate-300">
            <p>
              Tuesday 9 June 2026 is a serious date for anyone travelling to Ibiza for underground house music. Eastenderz lands at Hi Ibiza with a lineup that speaks directly to deep tech, minimal and rolling tech house fans.
            </p>
            <p>
              The Theatre brings East End Dubs, Patrick Topping, Wheats and Stef Davidse together under the Eastenderz banner. In the Club Room, NOW HERE adds Paco Osuna, Sosa and Gruuvelements, giving the night a second layer of late-night pressure.
            </p>
            <p>
              For HLT, this is exactly the kind of event worth building a trip around: clear sound, strong names, a proper Ibiza venue and a customer who knows why they are travelling.
            </p>
            <div className="flex flex-wrap gap-3">
              <TicketCta children="View tickets through Ibiza Spotlight" />
              <ConciergeCta children="Ask HLT to plan the full Ibiza setup" />
            </div>
          </section>

          <ArticleSection title="Why Eastenderz at Hi Ibiza matters">
            <p>
              Eastenderz has become one of the most recognised names in the UK-led deep tech and minimal house lane. Led by East End Dubs, the brand has built its reputation around rhythm, detail, clean grooves and a loyal crowd that prefers music-first clubbing over obvious commercial moments.
            </p>
            <p>
              That identity fits Ibiza perfectly. The island is not only about big EDM moments or generic party packages. A huge part of Ibiza's pull is still the feeling of finding the right room, the right sound and the right crowd.
            </p>
            <p>
              Hi Ibiza gives Eastenderz the scale, production and international reach to turn a Tuesday into a destination night. For travellers, that makes the event useful: you can lock in the Tuesday, then build the hotel, villa, flights, transfers and other club nights around it.
            </p>
          </ArticleSection>

          <ArticleSection title="The Theatre lineup: East End Dubs, Patrick Topping, Wheats and Stef Davidse">
            <p>
              The Theatre lineup is strong because it connects several different parts of the current house and tech house world. East End Dubs brings the Eastenderz sound and the identity of the night. Patrick Topping adds major UK house appeal and a bigger-room edge. Wheats brings sharp tech house pressure, while Stef Davidse keeps the lineup locked into the new-school underground lane.
            </p>
            <p>
              It is a lineup built for a crowd that wants the night to roll. Expect tight drums, low-end pressure, groove-led selections and a dancefloor that has a reason to stay until late.
            </p>
            <ul className="list-disc space-y-2 pl-6 text-slate-200">
              {THEATRE_LINEUP.map((artist) => <li key={artist}>{artist}</li>)}
            </ul>
          </ArticleSection>

          <ArticleSection title="Club Room / NOW HERE: Paco Osuna, Sosa and Gruuvelements">
            <p>
              The Club Room adds a second strong reason to go. Paco Osuna's NOW HERE concept gives the night another lane of Ibiza club music, with Sosa and Gruuvelements joining the bill.
            </p>
            <p>
              That is important for groups. Some people will be locked into Eastenderz in The Theatre, while others may drift toward the Club Room for a different kind of groove. Hi Ibiza works well for that because the venue gives you movement without losing the night.
            </p>
            <ul className="list-disc space-y-2 pl-6 text-slate-200">
              {CLUB_ROOM_LINEUP.map((artist) => <li key={artist}>{artist}</li>)}
            </ul>
          </ArticleSection>

          <ArticleSection title="Why 9 June is a smart Ibiza date">
            <p>
              Early June is one of the best windows for music-led Ibiza travel. The island is alive, the season has properly started, but accommodation pressure is often more manageable than peak August.
            </p>
            <p>
              A Tuesday event also gives you flexibility. You can fly in for a focused midweek trip, stretch it into a long weekend, or use Eastenderz as one of several nights across a full Ibiza week.
            </p>
            <p>
              The key is planning the event first. Once the Eastenderz ticket is locked in, everything else becomes easier: where to stay, how to move around, which beach clubs to visit, when to recover and where to go next.
            </p>
          </ArticleSection>

          <ArticleSection title="Build your Ibiza trip around Eastenderz">
            <p>
              HLT is built for people who travel for house music. Some guests only need the ticket link. Others want the full Ibiza plan handled cleanly so the group can focus on the trip, not the admin.
            </p>
            <p>For Eastenderz at Hi Ibiza, HLT can help you plan:</p>
            <ul className="list-disc space-y-2 pl-6 text-slate-200">
              <li>Eastenderz ticket direction through the event link</li>
              <li>Hotels close to Playa d'en Bossa, Ibiza Town, Talamanca or San Antonio</li>
              <li>Group villas for private pre-parties, pool days and recovery time</li>
              <li>Airport transfers and club transport</li>
              <li>VIP table, beach club and restaurant enquiries</li>
              <li>After-party and villa party planning for private groups</li>
              <li>Full Ibiza packages combining tickets, accommodation, transfers and concierge support</li>
            </ul>
            <div className="flex flex-wrap gap-3">
              <TicketCta children="View Eastenderz ticket options" />
              <ConciergeCta children="Request an Ibiza concierge package" />
            </div>
          </ArticleSection>

          <ArticleSection title="Where to stay for Hi Ibiza">
            <p>
              Hi Ibiza is in Playa d'en Bossa, so the easiest stay is nearby if you want minimum transport and maximum nightlife access. Playa d'en Bossa works well for groups who want Hi Ibiza, Ushuaia, beach days and straightforward taxis.
            </p>
            <p>
              Ibiza Town is better if you want restaurants, marina energy and a more balanced trip. Talamanca can feel more polished while still keeping you close to the action. San Antonio can work for budget-conscious groups, but transport needs to be planned properly.
            </p>
            <p>
              For bigger groups, a villa can make the whole trip feel more premium: pool days, private pre-drinks, space for everyone and a cleaner base for concierge planning.
            </p>
          </ArticleSection>

          <ArticleSection title="Eastenderz 9 June event details">
            <div className="grid gap-4 border border-white/10 bg-[#111111] p-5 text-sm text-slate-300 sm:grid-cols-2">
              <div><span className="block text-white">Event</span>East End Dubs presents Eastenderz</div>
              <div><span className="block text-white">Venue</span>Hi Ibiza</div>
              <div><span className="block text-white">Date</span>Tuesday 9 June 2026</div>
              <div><span className="block text-white">Time</span>23:30</div>
              <div><span className="block text-white">Theatre</span>East End Dubs, Patrick Topping, Wheats, Stef Davidse</div>
              <div><span className="block text-white">Club Room / NOW HERE</span>Paco Osuna, Sosa, Gruuvelements</div>
              <div><span className="block text-white">Music</span>Deep tech, minimal house, tech house and underground groove</div>
              <div><span className="block text-white">Travel support</span>Hotels, villas, transfers, VIP extras and concierge</div>
            </div>
          </ArticleSection>

          <ArticleSection title="Final word">
            <p>
              Eastenderz at Hi Ibiza on Tuesday 9 June 2026 is one of those dates that makes sense for serious house music travellers. It has the Eastenderz identity, the Hi Ibiza production level, a strong Theatre lineup and a Club Room programme that gives the night depth.
            </p>
            <p>
              If you are planning Ibiza in June, this is a clean date to build around. Book the ticket, choose the hotel or villa, sort the transfers and let the night become the reason the trip exists.
            </p>
            <div className="flex flex-wrap gap-3">
              <TicketCta children="View Eastenderz tickets" />
              <ConciergeCta children="Build the trip with HLT" />
              <Link to={EVENTS_URL} className="btn-secondary">Browse more Ibiza events -&gt;</Link>
            </div>
          </ArticleSection>

          <section className="space-y-4 border-t border-white/10 pt-8">
            <h2 className="font-display text-4xl leading-none text-white md:text-5xl">FAQ</h2>
            <div className="space-y-3">
              {FAQS.map((item) => (
                <details key={item.question} className="border border-white/10 bg-[#111111] p-4">
                  <summary className="cursor-pointer font-semibold text-white">{item.question}</summary>
                  <p className="mt-3 text-sm leading-7 text-slate-300">{item.answer}</p>
                </details>
              ))}
            </div>
          </section>
        </div>
      </article>
    </>
  );
}
